import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { ArrowLeft, ShieldAlert, Sparkles, Save, Beaker, RefreshCcw, Trash2, Clock, Ban, Check, X, AlertTriangle, Heart, Scale, Key, Eye, EyeOff } from "lucide-react";

const SEV_STYLE = {
  low:      { text: "text-[#8B5CF6]", border: "border-[#8B5CF6]/40", bg: "bg-[#8B5CF6]/10", dot: "bg-[#8B5CF6]" },
  medium:   { text: "text-[#F59E0B]", border: "border-[#F59E0B]/40", bg: "bg-[#F59E0B]/10", dot: "bg-[#F59E0B]" },
  high:     { text: "text-[#FF7F00]", border: "border-[#FF7F00]/40", bg: "bg-[#FF7F00]/10", dot: "bg-[#FF7F00]" },
  critical: { text: "text-[#EF4444]", border: "border-[#EF4444]/40", bg: "bg-[#EF4444]/15", dot: "bg-[#EF4444]" },
};

const OPENAI_MODEL_CHOICES = [
  { id: "gpt-4o-mini",   label: "gpt-4o-mini · fast, cheap (recommended)" },
  { id: "gpt-4o",        label: "gpt-4o · smart" },
  { id: "gpt-4.1-mini",  label: "gpt-4.1-mini" },
  { id: "gpt-4.1",       label: "gpt-4.1" },
  { id: "o4-mini",       label: "o4-mini · reasoning" },
];

function ByoOpenaiInput({ currentSet, currentModel, onSave }) {
  const [key, setKey] = useState("");
  const [show, setShow] = useState(false);
  const [model, setModel] = useState(currentModel || "gpt-4o-mini");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!key.trim()) return;
    setBusy(true);
    await onSave(key.trim(), model);
    setKey("");
    setBusy(false);
  };
  const clearKey = async () => {
    setBusy(true);
    await onSave("", null);
    setBusy(false);
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2 items-stretch">
        <div className="relative flex-1">
          <input
            data-testid="ai-openai-key-input"
            type={show ? "text" : "password"}
            value={key}
            onChange={e => setKey(e.target.value)}
            placeholder={currentSet ? "sk-… (leave blank to keep current)" : "sk-…"}
            autoComplete="off"
            spellCheck={false}
            className="w-full bg-[#06070A] border border-white/10 pl-3 pr-10 py-2 text-sm outline-none focus:border-[#8B5CF6] font-mono"
          />
          <button
            type="button"
            onClick={() => setShow(s => !s)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white"
            data-testid="ai-openai-key-toggle"
            title={show ? "Hide key" : "Show key"}
          >
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <select
          data-testid="ai-openai-model-select"
          value={model}
          onChange={e => setModel(e.target.value)}
          className="bg-[#06070A] border border-white/10 px-3 py-2 text-xs outline-none focus:border-[#8B5CF6] font-mono min-w-[240px]"
        >
          {OPENAI_MODEL_CHOICES.map(m => (
            <option key={m.id} value={m.id}>{m.label}</option>
          ))}
        </select>
      </div>
      <div className="flex gap-2">
        <button
          data-testid="ai-openai-save-btn"
          onClick={save}
          disabled={busy || !key.trim()}
          className="px-4 py-2 bg-[#8B5CF6] hover:bg-[#7C3AED] text-white font-display font-bold text-xs disabled:opacity-50 flex items-center gap-2"
        >
          <Save className="w-3.5 h-3.5" /> Save Key
        </button>
        {currentSet && (
          <button
            data-testid="ai-openai-clear-btn"
            onClick={clearKey}
            disabled={busy}
            className="px-4 py-2 border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10 font-display font-bold text-xs disabled:opacity-50 flex items-center gap-2"
          >
            <Trash2 className="w-3.5 h-3.5" /> Remove Key
          </button>
        )}
      </div>
    </div>
  );
}

export default function AIFlags() {
  const { guildId } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [flags, setFlags] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [wordsInput, setWordsInput] = useState("");
  const [scanText, setScanText] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [guildName, setGuildName] = useState("");
  const [appealsList, setAppealsList] = useState([]);
  const [roles, setRoles] = useState([]);
  const [aiModels, setAiModels] = useState([]);
  const [aiCategories, setAiCategories] = useState([
    { key: "toxicity",    label: "Toxicity",    desc: "Rude, insulting, mean-spirited language" },
    { key: "hate_speech", label: "Hate Speech", desc: "Slurs or attacks on a protected group" },
    { key: "harassment",  label: "Harassment",  desc: "Targeted attacks on a specific user" },
    { key: "threats",     label: "Threats",     desc: "Credible or serious threats of violence" },
    { key: "spam",        label: "Spam",        desc: "Repetitive junk, ad links, mass mentions" },
    { key: "nsfw",        label: "NSFW",        desc: "Sexual content or gore" },
    { key: "self_harm",   label: "Self-Harm",   desc: "User expressing self-harm ideation (never punished — DMs 988 resources)" },
  ]);

  const load = async () => {
    try {
      const [cfg, fls, guild, aps, mdls, cats] = await Promise.all([
        api.get(`/guilds/${guildId}/ai-config`),
        api.get(`/guilds/${guildId}/ai-flags`),
        api.get(`/guilds/${guildId}`).catch(() => ({ data: null })),
        api.get(`/guilds/${guildId}/appeals`).catch(() => ({ data: { appeals: [] } })),
        api.get(`/guilds/${guildId}/ai-models`).catch(() => ({ data: { models: [] } })),
        api.get(`/guilds/${guildId}/ai-categories`).catch(() => ({ data: { categories: [] } })),
      ]);
      setConfig(cfg.data.config);
      setFlags(fls.data.flags || []);
      setChannels(guild.data?.channels?.filter(c => c.type === 0) || []);
      // Filter out @everyone (id = guild_id) and managed/bot roles
      setRoles((guild.data?.roles || []).filter(r => r.id !== guild.data?.guild?.id && !r.managed).sort((a, b) => (b.position || 0) - (a.position || 0)));
      setGuildName(guild.data?.guild?.name || "");
      setWordsInput((cfg.data.config.custom_words || []).join(", "));
      setAppealsList(aps.data.appeals || []);
      setAiModels(mdls.data.models || []);
      if ((cats.data.categories || []).length) setAiCategories(cats.data.categories);
    } catch (e) {
      toast.error("Failed to load AI moderation");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [guildId]);

  const save = async () => {
    setSaving(true);
    try {
      const words = wordsInput.split(",").map(w => w.trim()).filter(Boolean);
      const r = await api.put(`/guilds/${guildId}/ai-config`, { ...config, custom_words: words });
      setConfig(r.data.config);
      toast.success("Config saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const scan = async () => {
    if (!scanText.trim()) return;
    setScanning(true);
    try {
      const r = await api.post(`/guilds/${guildId}/ai-scan`, { text: scanText });
      setScanResult(r.data.result);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally { setScanning(false); }
  };

  const takeAction = async (flag, action) => {
    const label = { dismiss: "dismiss", delete: "delete the message", warn: "warn the user", timeout: "timeout the user (10min)", ban: "BAN the user" }[action];
    if (action !== "dismiss" && !confirm(`Are you sure you want to ${label}?`)) return;
    try {
      await api.post(`/guilds/${guildId}/ai-flags/${flag.id}/action`, { action, minutes: 10 });
      toast.success(action === "dismiss" ? "Dismissed" : `${action} applied`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const resolveAppeal = async (appeal, approve) => {
    if (!confirm(approve ? "Approve appeal and UNDO the action?" : "Deny this appeal?")) return;
    try {
      const r = await api.post(`/guilds/${guildId}/appeals/${appeal.id}/resolve`, { approve });
      toast.success(approve ? `Approved · ${r.data.undone || 'done'}` : "Denied");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (loading) return <div className="min-h-screen bg-[#06070A] flex items-center justify-center text-[#8B5CF6] font-mono text-sm">// loading AI mod...</div>;
  const cfg = config || {};

  return (
    <div className="min-h-screen bg-[#06070A] mm-grid-bg text-white" data-testid="ai-flags-page">
      <div className="absolute inset-0 mm-noise pointer-events-none" />

      {/* Top bar */}
      <div className="relative z-10 border-b border-[#FF7F00]/20 bg-gradient-to-r from-[#FF7F00]/5 to-transparent flex items-center justify-between px-6 py-3">
        <div className="flex items-center gap-4">
          <button
            data-testid="ai-back-btn"
            onClick={() => navigate(`/dashboard/${guildId}`)}
            className="p-2 border border-white/10 hover:border-[#FF7F00] hover:text-[#FF7F00]"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-[#FF7F00]" />
              <span className="font-display font-black tracking-tight">AI MODERATOR</span>
              <span className="ml-2 px-2 py-0.5 border border-[#FF7F00]/40 bg-[#FF7F00]/10 text-[#FF7F00] font-mono text-[9px] tracking-widest flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> CLAUDE SONNET 4.6
              </span>
            </div>
            <div className="font-mono text-[10px] text-[#64748B] tracking-widest">
              {guildName ? guildName.toUpperCase() : "SERVER"} · {cfg.enabled ? "LIVE" : "DISABLED"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="ai-refresh-btn"
            onClick={load}
            className="p-2 border border-white/10 hover:border-[#FF7F00] hover:text-[#FF7F00]"
          >
            <RefreshCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Config */}
        <div className="border border-white/10 bg-[#0D0E15] p-6" data-testid="ai-config-card">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-[#FF7F00]" />
              <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Configuration</span>
            </div>
            <button
              data-testid="ai-config-save"
              onClick={save}
              disabled={saving}
              className="px-4 py-2 bg-[#FF7F00] hover:bg-[#FF9520] text-black font-display font-bold text-sm flex items-center gap-2 disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" /> {saving ? "SAVING..." : "SAVE"}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Toggles */}
            <Row label="Live Monitoring" hint="Bot reads every message and runs the classifier. Needs MESSAGE CONTENT intent enabled in Discord Developer Portal.">
              <Toggle testId="ai-toggle-enabled" enabled={!!cfg.enabled} onChange={v => setConfig(c => ({ ...c, enabled: v }))} />
            </Row>
            <Row label="AI Model" hint="Which AI powers moderation. Every option is free & unmetered for you. Faster tiers = quicker responses.">
              <select
                data-testid="ai-model-select"
                value={`${cfg.ai_provider || "anthropic"}::${cfg.ai_model || "claude-sonnet-4-6"}`}
                onChange={e => {
                  const [p, m] = e.target.value.split("::");
                  setConfig(c => ({ ...c, ai_provider: p, ai_model: m }));
                }}
                className="bg-[#06070A] border border-white/10 px-3 py-1.5 text-xs outline-none focus:border-[#FF7F00] font-mono min-w-[220px]"
              >
                {["anthropic", "openai", "gemini"].map(prov => {
                  const label = prov === "anthropic" ? "Anthropic — Claude" : prov === "openai" ? "OpenAI — GPT" : "Google — Gemini";
                  const items = aiModels.filter(m => m.provider === prov);
                  if (!items.length) return null;
                  return (
                    <optgroup key={prov} label={label}>
                      {items.map(m => (
                        <option key={m.model} value={`${m.provider}::${m.model}`} disabled={m.locked}>
                          {m.label}{m.recommended ? " · recommended" : ""}{m.tier ? ` · ${m.tier}` : ""}{m.pro_only ? " 👑 Pro" : ""}
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
              </select>
              <Sparkles className="w-3.5 h-3.5 text-[#8B5CF6] ml-2" />
            </Row>
            <Row label="AI Auto-Actions" hint="Let the AI autonomously warn / delete / timeout / kick / ban based on its recommendation. Self-harm ideation is never punished.">
              <Toggle testId="ai-toggle-autoactions" enabled={!!cfg.auto_actions_enabled} onChange={v => setConfig(c => ({ ...c, auto_actions_enabled: v }))} />
              <Sparkles className="w-3.5 h-3.5 text-[#FF7F00] ml-2" />
            </Row>
            <Row label="Auto-Delete (High+)" hint="Even without full auto-actions, still delete high/critical severity messages.">
              <Toggle testId="ai-toggle-autodelete" enabled={!!cfg.auto_delete_high} onChange={v => setConfig(c => ({ ...c, auto_delete_high: v }))} />
            </Row>
            <Row label="DM 988 on Self-Harm" hint="If AI detects self-harm ideation, DM the user with 988 Suicide & Crisis Lifeline resources.">
              <Toggle testId="ai-toggle-dm988" enabled={!!cfg.auto_dm_988} onChange={v => setConfig(c => ({ ...c, auto_dm_988: v }))} />
              <Heart className="w-3.5 h-3.5 text-[#EF4444] ml-2" />
            </Row>
            <Row label="Max Auto-Action Severity" hint="AI will never auto-execute actions ABOVE this severity — safety cap. e.g. 'high' means AI can auto-warn/delete/timeout, but any 'critical' still requires a mod.">
              <select
                data-testid="ai-max-severity"
                value={cfg.max_action_severity || "high"}
                onChange={e => setConfig(c => ({ ...c, max_action_severity: e.target.value }))}
                className="bg-[#06070A] border border-white/10 px-3 py-1.5 text-xs outline-none focus:border-[#FF7F00] font-mono"
              >
                <option value="low">low</option>
                <option value="medium">medium</option>
                <option value="high">high (recommended)</option>
                <option value="critical">critical (aggressive)</option>
              </select>
            </Row>
            <Row label="Auto-Timeout Duration" hint="How long timeouts issued by the AI last.">
              <select
                data-testid="ai-timeout-mins"
                value={cfg.timeout_minutes || 10}
                onChange={e => setConfig(c => ({ ...c, timeout_minutes: parseInt(e.target.value, 10) }))}
                className="bg-[#06070A] border border-white/10 px-3 py-1.5 text-xs outline-none focus:border-[#FF7F00] font-mono"
              >
                <option value="5">5 min</option>
                <option value="10">10 min</option>
                <option value="30">30 min</option>
                <option value="60">1 hour</option>
                <option value="360">6 hours</option>
                <option value="1440">24 hours</option>
              </select>
            </Row>
            <Row label="Notify Severity ≥" hint="Only send mod-channel alerts at or above this severity.">
              <select
                data-testid="ai-severity-select"
                value={cfg.min_severity_notify || "medium"}
                onChange={e => setConfig(c => ({ ...c, min_severity_notify: e.target.value }))}
                className="bg-[#06070A] border border-white/10 px-3 py-1.5 text-xs outline-none focus:border-[#FF7F00] font-mono"
              >
                <option value="low">low</option>
                <option value="medium">medium</option>
                <option value="high">high</option>
                <option value="critical">critical</option>
              </select>
            </Row>
            <Row label="Appeal Button in DMs" hint="After every AI auto-action, DM the user with an ⚖ Appeal button. Clicking it opens a modal → posts an appeal ticket to your appeals channel with Approve/Deny buttons that auto-undo the action.">
              <Toggle testId="ai-toggle-appeals" enabled={!!cfg.appeals_enabled} onChange={v => setConfig(c => ({ ...c, appeals_enabled: v }))} />
              <Scale className="w-3.5 h-3.5 text-[#8B5CF6] ml-2" />
            </Row>
          </div>

          <div className="mt-5 space-y-4">
            <div>
              <label className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2 block">Notify Channel</label>
              <select
                data-testid="ai-notify-channel"
                value={cfg.notify_channel_id || ""}
                onChange={e => setConfig(c => ({ ...c, notify_channel_id: e.target.value || null }))}
                className="w-full bg-[#06070A] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#FF7F00] font-mono"
              >
                <option value="">— no channel · flags stored only —</option>
                {channels.map(c => (
                  <option key={c.id} value={c.id}>#{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-widest text-[#8B5CF6] uppercase mb-2 flex items-center gap-1">
                <Scale className="w-3 h-3" /> Appeals Channel
              </label>
              <select
                data-testid="ai-appeals-channel"
                value={cfg.appeals_channel_id || ""}
                onChange={e => setConfig(c => ({ ...c, appeals_channel_id: e.target.value || null }))}
                className="w-full bg-[#06070A] border border-[#8B5CF6]/20 px-3 py-2 text-sm outline-none focus:border-[#8B5CF6] font-mono"
              >
                <option value="">— no channel · appeals disabled —</option>
                {channels.map(c => (
                  <option key={c.id} value={c.id}>#{c.name}</option>
                ))}
              </select>
              <div className="mt-1 font-mono text-[10px] text-[#64748B]">
                // appeal tickets get posted here with Approve/Deny buttons · needs manage_server perms to click
              </div>
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2 block">
                Custom Flagged Words · comma-separated
              </label>
              <input
                data-testid="ai-custom-words"
                value={wordsInput}
                onChange={e => setWordsInput(e.target.value)}
                placeholder="slur1, discord.gg/spamlink, raid"
                className="w-full bg-[#06070A] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#FF7F00] font-mono"
              />
              <div className="mt-1 font-mono text-[10px] text-[#64748B]">
                // any message containing these will auto-flag at ≥medium severity (case-insensitive)
              </div>
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-widest text-[#10B981] uppercase mb-2 block flex items-center gap-1.5">
                <Check className="w-3 h-3" /> Trusted Roles · skip AI moderation
              </label>
              <div data-testid="ai-whitelist-roles" className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-2 border border-white/10 bg-[#06070A] rounded-md">
                {roles.length === 0 && <span className="text-xs text-[#64748B]">No roles found. Try refreshing.</span>}
                {roles.map(r => {
                  const active = (cfg.whitelist_role_ids || []).includes(r.id);
                  return (
                    <button
                      key={r.id}
                      type="button"
                      data-testid={`whitelist-role-${r.id}`}
                      onClick={async () => {
                        const next = active
                          ? (cfg.whitelist_role_ids || []).filter(x => x !== r.id)
                          : [...(cfg.whitelist_role_ids || []), r.id];
                        // Optimistic UI
                        setConfig(c => ({ ...c, whitelist_role_ids: next }));
                        try {
                          const resp = await api.put(`/guilds/${guildId}/ai-config`, { whitelist_role_ids: next });
                          setConfig(resp.data.config);
                          toast.success(active ? `@${r.name} · removed from trusted` : `@${r.name} · added to trusted`);
                        } catch (e) {
                          // Roll back
                          setConfig(c => ({ ...c, whitelist_role_ids: cfg.whitelist_role_ids || [] }));
                          toast.error(e?.response?.data?.detail || "Failed to update trusted roles");
                        }
                      }}
                      className={`px-2 py-1 rounded-md text-xs transition-colors border ${
                        active
                          ? "bg-[#10B981]/15 border-[#10B981]/50 text-[#10B981]"
                          : "bg-transparent border-white/10 text-[#94A3B8] hover:border-white/25"
                      }`}
                      style={r.color && !active ? { color: `#${r.color.toString(16).padStart(6, '0')}` } : undefined}
                    >
                      @{r.name}
                    </button>
                  );
                })}
              </div>
              <div className="mt-1 font-mono text-[10px] text-[#64748B]">
                click a role to toggle · saves instantly · members with any trusted role bypass AI moderation entirely
              </div>
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-widest text-[#EF4444] uppercase mb-2 block flex items-center gap-1.5">
                <X className="w-3 h-3" /> Excluded Channels · skip AI moderation
              </label>
              <div data-testid="ai-excluded-channels" className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-2 border border-white/10 bg-[#06070A] rounded-md">
                {channels.length === 0 && <span className="text-xs text-[#64748B]">No text channels found. Try refreshing.</span>}
                {channels.map(c => {
                  const active = (cfg.excluded_channels || []).includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      data-testid={`excluded-channel-${c.id}`}
                      onClick={async () => {
                        const next = active
                          ? (cfg.excluded_channels || []).filter(x => x !== c.id)
                          : [...(cfg.excluded_channels || []), c.id];
                        setConfig(cfg2 => ({ ...cfg2, excluded_channels: next }));
                        try {
                          const resp = await api.put(`/guilds/${guildId}/ai-config`, { excluded_channels: next });
                          setConfig(resp.data.config);
                          toast.success(active ? `#${c.name} · re-enabled AI` : `#${c.name} · AI disabled here`);
                        } catch (e) {
                          setConfig(cfg2 => ({ ...cfg2, excluded_channels: cfg.excluded_channels || [] }));
                          toast.error(e?.response?.data?.detail || "Failed to update excluded channels");
                        }
                      }}
                      className={`px-2 py-1 rounded-md text-xs transition-colors border ${
                        active
                          ? "bg-[#EF4444]/15 border-[#EF4444]/50 text-[#EF4444]"
                          : "bg-transparent border-white/10 text-[#94A3B8] hover:border-white/25"
                      }`}
                    >
                      #{c.name}
                    </button>
                  );
                })}
              </div>
              <div className="mt-1 font-mono text-[10px] text-[#64748B]">
                click a channel to toggle · saves instantly · messages posted here bypass AI moderation entirely
              </div>
            </div>

          </div>
        </div>

        {/* Excluded Categories — chip picker */}
        <div className="border border-[#F59E0B]/20 bg-[#0D0E15] p-6" data-testid="ai-excluded-categories-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-[#F59E0B]" />
              <span className="font-mono text-[10px] tracking-widest text-[#F59E0B] uppercase">Excluded Categories · skip AI moderation</span>
            </div>
            <span className="font-mono text-[10px] text-[#64748B]">
              {(cfg.excluded_categories || []).length} of {aiCategories.length} ignored
            </span>
          </div>
          <div data-testid="ai-excluded-categories" className="flex flex-wrap gap-1.5 p-2 border border-white/10 bg-[#06070A] rounded-md">
            {aiCategories.map(cat => {
              const active = (cfg.excluded_categories || []).includes(cat.key);
              return (
                <button
                  key={cat.key}
                  type="button"
                  data-testid={`excluded-category-${cat.key}`}
                  title={cat.desc}
                  onClick={async () => {
                    const next = active
                      ? (cfg.excluded_categories || []).filter(x => x !== cat.key)
                      : [...(cfg.excluded_categories || []), cat.key];
                    setConfig(c2 => ({ ...c2, excluded_categories: next }));
                    try {
                      const resp = await api.put(`/guilds/${guildId}/ai-config`, { excluded_categories: next });
                      setConfig(resp.data.config);
                      toast.success(active ? `${cat.label} · now enforced` : `${cat.label} · now ignored`);
                    } catch (e) {
                      setConfig(c2 => ({ ...c2, excluded_categories: cfg.excluded_categories || [] }));
                      toast.error(e?.response?.data?.detail || "Failed to update excluded categories");
                    }
                  }}
                  className={`px-2 py-1 rounded-md text-xs transition-colors border ${
                    active
                      ? "bg-[#F59E0B]/15 border-[#F59E0B]/50 text-[#F59E0B]"
                      : "bg-transparent border-white/10 text-[#94A3B8] hover:border-white/25"
                  }`}
                >
                  #{cat.label}
                </button>
              );
            })}
          </div>
          <div className="mt-1 font-mono text-[10px] text-[#64748B]">
            click a category to toggle · saves instantly · flags matching ONLY excluded categories are silently dropped
          </div>
        </div>

        {/* BYO OpenAI Key — bring your own OpenAI account (bypasses internal key) */}
        <div className="border border-[#8B5CF6]/25 bg-[#0D0E15] p-6" data-testid="ai-byok-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Key className="w-4 h-4 text-[#8B5CF6]" />
              <span className="font-mono text-[10px] tracking-widest text-[#8B5CF6] uppercase">Your Own OpenAI Key · optional BYOK</span>
            </div>
            {cfg.openai_api_key_set && (
              <span className="font-mono text-[10px] text-[#10B981] flex items-center gap-1">
                <Check className="w-3 h-3" /> key active · {cfg.openai_api_key}
              </span>
            )}
          </div>
          <p className="text-xs text-[#94A3B8] mb-3 leading-relaxed">
            Paste your own <span className="text-[#8B5CF6]">OpenAI API key</span> to route AI moderation through your account instead of the built-in one.
            You'll be billed by OpenAI directly. Leave blank to use the free built-in classifier.
          </p>
          <ByoOpenaiInput
            currentSet={!!cfg.openai_api_key_set}
            currentModel={cfg.openai_model || "gpt-4o-mini"}
            onSave={async (key, model) => {
              try {
                const patch = {};
                if (key !== null) patch.openai_api_key = key;
                if (model) patch.openai_model = model;
                const r = await api.put(`/guilds/${guildId}/ai-config`, patch);
                setConfig(r.data.config);
                toast.success(key === "" ? "OpenAI key removed" : "OpenAI key saved");
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Failed to save OpenAI key");
              }
            }}
          />
          <div className="mt-2 font-mono text-[10px] text-[#64748B]">
            // your key is stored server-side and never returned in plaintext · get one at platform.openai.com/api-keys
          </div>
        </div>

        {/* Test scan */}
        <div className="border border-[#10B981]/20 bg-gradient-to-br from-[#10B981]/5 via-[#0D0E15] to-[#0D0E15] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Beaker className="w-4 h-4 text-[#10B981]" />
            <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Test Scan</span>
          </div>
          <div className="flex gap-2">
            <input
              data-testid="ai-scan-input"
              value={scanText}
              onChange={e => setScanText(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !scanning && scan()}
              placeholder="Paste any text to see how the AI would classify it..."
              className="flex-1 bg-[#06070A] border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[#10B981] font-mono"
            />
            <button
              data-testid="ai-scan-btn"
              onClick={scan}
              disabled={scanning || !scanText.trim()}
              className="px-5 py-2.5 bg-[#10B981] text-black font-display font-bold text-sm hover:bg-[#dfff33] disabled:opacity-50"
            >
              {scanning ? "SCANNING..." : "SCAN"}
            </button>
          </div>
          {scanResult && (
            <div data-testid="ai-scan-result" className="mt-4 border border-white/10 bg-[#06070A] p-4">
              <ScanResultView r={scanResult} />
            </div>
          )}
        </div>

        {/* Appeals feed */}
        <div className="border border-[#8B5CF6]/20 bg-[#0D0E15]">
          <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-[#8B5CF6]/5 to-transparent">
            <div className="flex items-center gap-2">
              <Scale className="w-4 h-4 text-[#8B5CF6]" />
              <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Appeals · {appealsList.length}</span>
            </div>
            <div className="font-mono text-[10px] text-[#64748B]">
              {appealsList.filter(a => a.status === "pending").length} pending
            </div>
          </div>
          <div className="max-h-[50vh] overflow-y-auto" data-testid="appeals-list">
            {appealsList.length === 0 && (
              <div className="p-8 text-center font-mono text-xs text-[#64748B]">
                // no appeals yet · enable auto-actions + appeals to start receiving them
              </div>
            )}
            {appealsList.map(a => <AppealCard key={a.id} appeal={a} onResolve={resolveAppeal} />)}
          </div>
        </div>

        {/* Flag feed */}
        <div className="border border-white/10 bg-[#0D0E15]">
          <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-[#FF7F00]" />
              <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Recent Flags · {flags.length}</span>
            </div>
            <div className="font-mono text-[10px] text-[#64748B]">
              {flags.filter(f => !f.resolved).length} unresolved
            </div>
          </div>
          <div className="max-h-[70vh] overflow-y-auto" data-testid="ai-flag-list">
            {flags.length === 0 && (
              <div className="p-8 text-center font-mono text-xs text-[#64748B]">
                // no flags yet · enable live monitoring or test the scanner
              </div>
            )}
            {flags.map(f => <FlagCard key={f.id} flag={f} onAction={takeAction} />)}
          </div>
        </div>
      </div>
    </div>
  );
}

function ScanResultView({ r }) {
  const style = SEV_STYLE[r.severity] || SEV_STYLE.low;
  return (
    <div>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className={`px-2 py-0.5 border ${style.border} ${style.bg} ${style.text} font-mono text-[10px] tracking-widest`}>{r.severity?.toUpperCase()}</span>
        <span className={`font-mono text-[10px] tracking-widest ${r.flagged ? "text-[#FF7F00]" : "text-[#10B981]"}`}>
          {r.flagged ? "FLAGGED" : "CLEAN"}
        </span>
        {r.recommended_action && r.recommended_action !== "none" && (
          <span className="px-2 py-0.5 border border-[#FF7F00]/40 bg-[#FF7F00]/10 text-[#FF7F00] font-mono text-[10px] tracking-widest flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> AI → {r.recommended_action.toUpperCase()}
          </span>
        )}
        {r.self_harm_ideation && (
          <span className="px-2 py-0.5 border border-[#EF4444]/50 bg-[#EF4444]/10 text-[#EF4444] font-mono text-[10px] tracking-widest flex items-center gap-1">
            <Heart className="w-3 h-3" /> SELF-HARM · 988 DM
          </span>
        )}
      </div>
      {r.categories?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {r.categories.map(c => (
            <span key={c} className="px-1.5 py-0.5 border border-white/10 font-mono text-[9px] text-[#94A3B8]">{c}</span>
          ))}
        </div>
      )}
      {r.matched_words?.length > 0 && (
        <div className="mb-2 font-mono text-[10px] text-[#F59E0B]">
          matched word list: {r.matched_words.join(", ")}
        </div>
      )}
      <div className="text-sm text-[#E2E8F0]">{r.reason}</div>
    </div>
  );
}

function FlagCard({ flag, onAction }) {
  const c = flag.classification || {};
  const style = SEV_STYLE[c.severity] || SEV_STYLE.low;
  return (
    <div
      data-testid={`flag-card-${flag.id}`}
      className={`p-4 border-b border-white/5 hover:bg-[#141622] ${flag.resolved ? "opacity-50" : ""}`}
    >
      <div className="flex items-start gap-3">
        <span className={`shrink-0 w-2 h-2 rounded-full mt-2 ${style.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`px-2 py-0.5 border ${style.border} ${style.bg} ${style.text} font-mono text-[9px] tracking-widest`}>{c.severity?.toUpperCase()}</span>
            <span className="font-medium text-sm">{flag.author_name || flag.author_id}</span>
            <span className="font-mono text-[10px] text-[#64748B]">·</span>
            <span className="font-mono text-[10px] text-[#64748B]">{new Date(flag.created_at).toLocaleString()}</span>
            {c.recommended_action && c.recommended_action !== "none" && (
              <span className="px-1.5 py-0.5 border border-[#FF7F00]/40 bg-[#FF7F00]/10 text-[#FF7F00] font-mono text-[9px] tracking-widest flex items-center gap-1">
                <Sparkles className="w-2.5 h-2.5" /> {c.recommended_action.toUpperCase()}
              </span>
            )}
            {c.self_harm_ideation && (
              <span className="px-1.5 py-0.5 border border-[#EF4444]/40 bg-[#EF4444]/10 text-[#EF4444] font-mono text-[9px] tracking-widest flex items-center gap-1">
                <Heart className="w-2.5 h-2.5" /> 988
              </span>
            )}
            {flag.crisis_dm_sent && <span className="font-mono text-[9px] text-[#10B981]">✓ DM sent</span>}
            {flag.action_taken && <span className="px-1.5 py-0.5 border border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981] font-mono text-[9px] tracking-widest">⚡ AUTO: {flag.action_taken.toUpperCase()}</span>}
          </div>
          <div className="text-sm text-[#E2E8F0] mb-1.5 whitespace-pre-wrap break-words">
            &ldquo;{flag.content}&rdquo;
          </div>
          <div className="text-xs text-[#94A3B8] mb-2">
            {c.reason}
            {c.categories?.length > 0 && <span className="ml-2 font-mono text-[10px]">· {c.categories.join(", ")}</span>}
          </div>
          {!flag.resolved && (
            <div className="flex flex-wrap gap-1.5">
              <ActionBtn testId={`flag-${flag.id}-dismiss`} onClick={() => onAction(flag, "dismiss")} icon={Check} color="cyan">DISMISS</ActionBtn>
              <ActionBtn testId={`flag-${flag.id}-delete`} onClick={() => onAction(flag, "delete")} icon={Trash2} color="warn">DELETE MSG</ActionBtn>
              <ActionBtn testId={`flag-${flag.id}-warn`} onClick={() => onAction(flag, "warn")} icon={AlertTriangle} color="warn">WARN</ActionBtn>
              <ActionBtn testId={`flag-${flag.id}-timeout`} onClick={() => onAction(flag, "timeout")} icon={Clock} color="warn">TIMEOUT 10M</ActionBtn>
              <ActionBtn testId={`flag-${flag.id}-ban`} onClick={() => onAction(flag, "ban")} icon={Ban} color="danger">BAN</ActionBtn>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AppealCard({ appeal, onResolve }) {
  const pending = appeal.status === "pending";
  const statusStyle = {
    pending:  { text: "text-[#F59E0B]", bg: "bg-[#F59E0B]/10", border: "border-[#F59E0B]/40" },
    approved: { text: "text-[#10B981]", bg: "bg-[#10B981]/10", border: "border-[#10B981]/40" },
    denied:   { text: "text-[#EF4444]", bg: "bg-[#EF4444]/10", border: "border-[#EF4444]/40" },
  }[appeal.status] || { text: "text-white/60", bg: "bg-white/5", border: "border-white/20" };
  return (
    <div
      data-testid={`appeal-card-${appeal.id}`}
      className={`p-4 border-b border-white/5 hover:bg-[#141622] ${!pending ? "opacity-70" : ""}`}
    >
      <div className="flex items-start gap-3">
        <Scale className="w-5 h-5 text-[#8B5CF6] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <span className={`px-2 py-0.5 border ${statusStyle.border} ${statusStyle.bg} ${statusStyle.text} font-mono text-[9px] tracking-widest uppercase`}>
              {appeal.status}
            </span>
            <span className="font-medium text-sm">{appeal.user_name || appeal.user_id}</span>
            <span className="font-mono text-[10px] text-[#64748B]">·</span>
            <span className="font-mono text-[10px] text-[#64748B]">{new Date(appeal.created_at).toLocaleString()}</span>
            {appeal.undo_result && (
              <span className="font-mono text-[9px] text-[#10B981]">→ {appeal.undo_result}</span>
            )}
          </div>
          <div className="text-sm text-[#E2E8F0] mb-2 whitespace-pre-wrap break-words">
            &ldquo;{appeal.appeal_reason}&rdquo;
          </div>
          {pending && (
            <div className="flex flex-wrap gap-1.5">
              <button
                data-testid={`appeal-${appeal.id}-approve`}
                onClick={() => onResolve(appeal, true)}
                className="inline-flex items-center gap-1.5 px-3 py-1 border border-[#10B981]/40 hover:border-[#10B981] hover:bg-[#10B981]/10 text-[#10B981] font-mono text-[10px] tracking-widest transition-colors"
              >
                <Check className="w-3 h-3" /> APPROVE · UNDO
              </button>
              <button
                data-testid={`appeal-${appeal.id}-deny`}
                onClick={() => onResolve(appeal, false)}
                className="inline-flex items-center gap-1.5 px-3 py-1 border border-[#EF4444]/30 hover:border-[#EF4444] hover:text-[#EF4444] font-mono text-[10px] tracking-widest transition-colors"
              >
                <X className="w-3 h-3" /> DENY
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ActionBtn({ children, onClick, icon: Icon, color, testId }) {
  const colors = {
    cyan:   "border-[#8B5CF6]/30 hover:border-[#8B5CF6] hover:text-[#8B5CF6]",
    warn:   "border-[#F59E0B]/30 hover:border-[#F59E0B] hover:text-[#F59E0B]",
    danger: "border-[#EF4444]/30 hover:border-[#EF4444] hover:text-[#EF4444]",
  };
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 border ${colors[color]} font-mono text-[10px] tracking-widest transition-colors`}
    >
      <Icon className="w-3 h-3" /> {children}
    </button>
  );
}

function Row({ label, hint, children }) {
  return (
    <div className="border border-white/10 bg-[#06070A] p-3">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <span className="font-medium text-sm">{label}</span>
        <div className="flex items-center min-w-0 max-w-full [&_select]:max-w-full">{children}</div>
      </div>
      {hint && <div className="text-xs text-[#94A3B8] leading-relaxed">{hint}</div>}
    </div>
  );
}

function Toggle({ enabled, onChange, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      role="switch"
      aria-checked={enabled}
      onClick={() => onChange(!enabled)}
      className={`relative shrink-0 w-11 h-6 border transition-all duration-200 ${
        enabled ? "bg-[#FF7F00] border-[#FF7F00] shadow-[0_0_10px_rgba(255,127,0,0.4)]" : "bg-[#141622] border-white/15"
      }`}
    >
      <span
        className={`absolute top-0.5 w-4 h-4 transition-all duration-200 ${
          enabled ? "left-6 bg-black" : "left-0.5 bg-white/80"
        }`}
      />
    </button>
  );
}
